import useModifiers from 'hooks/useModifiers'
import useProducts from 'hooks/useProducts'
import useStableStringArray from 'hooks/useStableStringArray'
import { Fields as Modifier } from 'models/Modifier'
import { useCallback, useEffect, useMemo, useState } from 'react'

export function useModifierSelectData(
  selectedIds: string[],
  productIds: string[],
  setValue: (val: string[]) => void
) {
  const [searchTerm, setSearchTerm] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [page, setPage] = useState(1)
  const [selectedModifiers, setSelectedModifiers] = useState<Modifier[]>([])

  const stableProductIds = useStableStringArray(productIds)

  // Reset the pagination data when the search term or productIds change
  useEffect(() => setPage(1), [searchTerm, stableProductIds])
  // Reset the pagination data when the search term or productIds change
  useEffect(() => setSearchTerm(''), [stableProductIds])

  const isModifierSelectedBy = (idArray: string[]) => (modifier: Modifier) =>
    idArray.includes(modifier.option.chain_uid)

  const fetchModifierParams = useMemo(
    () => ({
      search_term: searchTerm,
      page,
      item_ids: productIds,
    }),
    [searchTerm, page, productIds]
  )

  const { modifiers, pagination, isReady, isLoading, error } = useModifiers(
    fetchModifierParams,
    {
      shouldSkip: !productIds.length,
      paginationKey: 'all',
    }
  )

  const deselectModifiers = useCallback(
    (modifiersList, idsList) => {
      const idsToDeselect = idsList.filter(id => {
        const modifier = modifiersList.find(
          modifier => modifier.option.chain_uid === id
        )
        if (!modifier) return false

        return !modifier.items.some(item => productIds.includes(item.chain_uid))
      })

      if (idsToDeselect.length) {
        const newSelectedIds = idsList.filter(id => !idsToDeselect.includes(id))
        setValue(newSelectedIds)

        // Update selectedModifiers by removing the deselected ones
        setSelectedModifiers(prev =>
          prev.filter(
            modifier => !idsToDeselect.includes(modifier.option.chain_uid)
          )
        )
      }
    },
    [productIds, setValue, setSelectedModifiers]
  )

  // If the product associated with a modifier is removed, deselect the modifier
  useEffect(() => {
    if (!productIds.length && !selectedIds.length) return

    if (selectedModifiers.length)
      return deselectModifiers(selectedModifiers, selectedIds)
    if (selectedIds.length && modifiers.length && isReady)
      return deselectModifiers(modifiers, selectedIds)
  }, [
    productIds,
    selectedModifiers,
    selectedIds,
    modifiers,
    isReady,
    setValue,
    deselectModifiers,
  ])

  // If this component is instantiated with selectedIds that are not in the selectedModifiers list,
  // fetch the missing modifiers
  const missingSelectedIds = useMemo(() => {
    if (!modifiers.length || !selectedIds.length || !isReady) return []
    return selectedIds.filter(
      id => !modifiers.find(modifier => modifier.option.chain_uid === id)
    )
  }, [selectedIds, modifiers, isReady])

  useModifiers(
    { option_ids: missingSelectedIds },
    {
      shouldSkip: !missingSelectedIds.length,
      preventCancellation: true,
      paginationKey: 'selected',
    }
  )

  // If this component is instantiated with selectedIds that are not in the selectedModifiers list,
  // link the selectedModifiers to the selectedIds
  useEffect(() => {
    if (!selectedIds.length || !modifiers.length || !isReady) return

    const foundModifiers = selectedIds
      .map(id => modifiers.find(modifier => modifier.option.chain_uid === id))
      .filter(Boolean) as Modifier[]

    if (
      foundModifiers.length &&
      foundModifiers.length !== selectedModifiers.length
    ) {
      setSelectedModifiers(foundModifiers)
    }
  }, [selectedIds, modifiers, isReady, selectedModifiers.length])

  const { shouldDisplayIds: displayProductIdInput } = useProducts()
  const noModifiersFound = !modifiers.length && !searchTerm && page === 1
  const shouldDisplayIdInput =
    displayProductIdInput || (isReady && (error || noModifiersFound))

  return {
    modifiersData: {
      modifiers,
      pagination,
      isReady,
      isLoading,
      error,
    },
    searchState: {
      searchTerm,
      setSearchTerm,
      page,
      setPage,
      isFocused,
      setIsFocused,
    },
    selection: {
      selectedModifiers,
      setSelectedModifiers,
      isModifierSelectedBy,
    },
    uiState: {
      shouldDisplayIdInput,
    },
  }
}
